import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Loader2, Leaf, Users, Recycle, AlertTriangle, BookOpen } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar } from "recharts";
import { getPuntoLimpioEstadisticas } from "@/api";

const StatCard = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5 text-center" data-testid={testId}>
    <Icon className="w-6 h-6 text-municipal mx-auto mb-2" />
    <div className="text-2xl font-bold text-municipal">{value}</div>
    <div className="text-xs text-gray-500">{label}</div>
  </div>
);

export default function PuntoLimpioEstadisticas() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("sfh_admin_session")) {
      navigate("/admin");
      return;
    }
    getPuntoLimpioEstadisticas().then(setStats).catch((err) => {
      if (err?.response?.status === 401) { navigate("/admin"); return; }
      setError(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="pt-28 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h1 className="font-heading font-bold text-3xl text-municipal mb-2">Punto Limpio — Estadísticas internas</h1>
          <p className="text-gray-500 mb-4">
            Tendencia de visitas, materiales recogidos e indicadores ambientales del Punto Limpio
            municipal, visible solo desde el backoffice.
          </p>
          <a href="/documentos/manual-usuario-backoffice.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-municipal text-sm mb-10 hover:underline" data-testid="link-manual-estadisticas">
            <BookOpen className="w-4 h-4" /> Consultar el manual (PDF)
          </a>

          {error ? (
            <div className="text-center py-20 text-gray-400" data-testid="estadisticas-error">
              No se han podido cargar las estadísticas. Inténtalo de nuevo más tarde.
            </div>
          ) : !stats ? (
            <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-municipal" /></div>
          ) : (
            <div data-testid="puntolimpio-estadisticas-content">
              <div className="grid sm:grid-cols-3 gap-4 mb-10">
                <StatCard icon={Users} label="Visitas de entrada registradas" value={stats.totales.visitas_entrada} testId="stat-visitas" />
                <StatCard icon={Leaf} label="Ciudadanos únicos" value={stats.totales.ciudadanos_unicos} testId="stat-ciudadanos" />
                <StatCard icon={Recycle} label="Apuntados a bonificación" value={stats.totales.apuntados_bonificacion} testId="stat-apuntados" />
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6 mb-10">
                <h2 className="font-heading font-semibold text-lg text-municipal mb-4">Visitas por mes (últimos 12 meses)</h2>
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={stats.tendencia_mensual}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="mes" fontSize={12} />
                    <YAxis fontSize={12} allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="visitas" stroke="#BD3742" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid sm:grid-cols-2 gap-6 mb-10">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h2 className="font-heading font-semibold text-lg text-municipal mb-4">Top materiales recogidos</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.por_material.slice(0, 8)} layout="vertical" margin={{ left: 40 }}>
                      <XAxis type="number" fontSize={11} />
                      <YAxis dataKey="material" type="category" fontSize={10} width={130} />
                      <Tooltip formatter={(v, _n, p) => [`${v} ${p.payload.unidad}`, "Total"]} />
                      <Bar dataKey="total" fill="#009940" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col">
                  <h2 className="font-heading font-semibold text-lg text-municipal mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" /> Indicador ambiental
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">Proporción de aportaciones clasificadas como peligrosas frente a no peligrosas.</p>
                  <div className="flex-1 flex flex-col justify-center gap-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">No peligrosos</span>
                        <span className="font-semibold text-valoriza-green">{stats.indicador_ambiental.no_peligroso_pct}%</span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-valoriza-green" style={{ width: `${stats.indicador_ambiental.no_peligroso_pct}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Peligrosos</span>
                        <span className="font-semibold text-amber-600">{stats.indicador_ambiental.peligroso_pct}%</span>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500" style={{ width: `${stats.indicador_ambiental.peligroso_pct}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400">Periodo mostrado: {stats.periodo.desde} — {stats.periodo.hasta}</p>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
